import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { MessageSquareOff, ShieldAlert, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";

export default function WhatsAppControl() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    try { const { data } = await api.get("/whatsapp-control/status"); setS(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Access denied"); setS({ enabled: false, state: "DISABLED" }); }
  };
  useEffect(() => { load(); }, []);
  const disable = async () => {
    setBusy(true);
    try { const { data } = await api.post("/whatsapp-control/disable", {});
      toast.success(`WhatsApp disabled · ${data.pending_cancelled} pending cancelled`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  const enable = async () => {
    if (!window.confirm("Re-enable WhatsApp API? Old queued/cancelled messages will NOT be resumed automatically.")) return;
    setBusy(true);
    try { await api.post("/whatsapp-control/enable"); toast.success("WhatsApp re-enabled"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  if (!s) return <div className="p-8 text-slate-500">Loading…</div>;
  const off = !s.enabled;
  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6" data-testid="whatsapp-control-page">
      <div>
        <div className="text-[11px] uppercase tracking-[0.16em] text-slate-400">ONE Connect · WhatsApp</div>
        <h1 className="text-2xl font-heading font-bold text-slate-900">WhatsApp API Control</h1>
      </div>
      <div className={`rounded-lg border p-5 ${off ? "border-rose-200 bg-rose-50" : "border-emerald-200 bg-emerald-50"}`}>
        <div className="flex items-center gap-3">
          {off ? <MessageSquareOff className="h-8 w-8 text-rose-600" /> : <CheckCircle2 className="h-8 w-8 text-emerald-600" />}
          <div>
            <div className={`text-lg font-bold ${off ? "text-rose-700" : "text-emerald-700"}`} data-testid="wa-state">
              {off ? "TEMPORARILY DISABLED" : "ENABLED"}
            </div>
            <div className="text-[12px] text-slate-600">
              {off ? "WhatsApp API is temporarily disabled by Super Admin. No messages will be sent from SUNTRONIX ONE."
                   : "WhatsApp API is active. Outbound messages are allowed."}
            </div>
          </div>
        </div>
        {off && (s.reason || s.changed_by) && (
          <div className="text-[11px] text-slate-500 mt-3 border-t border-rose-200 pt-2">
            Reason: {s.reason || "—"} · By {s.changed_by || "—"} · {s.changed_at ? s.changed_at.slice(0, 16).replace("T", " ") : "—"}
            {typeof s.pending_cancelled === "number" && ` · ${s.pending_cancelled} pending message(s) cancelled`}
          </div>
        )}
      </div>
      <div className="flex gap-3">
        {off ? (
          <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={enable} disabled={busy} data-testid="wa-enable-btn">
            <CheckCircle2 className="h-4 w-4 mr-1" /> Re-enable (controlled)
          </Button>
        ) : (
          <Button className="bg-rose-600 hover:bg-rose-700 text-white" onClick={disable} disabled={busy} data-testid="wa-disable-btn">
            <ShieldAlert className="h-4 w-4 mr-1" /> Disable WhatsApp now
          </Button>
        )}
        <Button variant="outline" onClick={load}>Refresh</Button>
      </div>
      <p className="text-[11px] text-slate-400">
        This is a server-side master kill switch: while disabled, every outbound WhatsApp path (manual sends, automations,
        campaigns, retries, webhooks) is blocked at the API level and returns “WHATSAPP INTEGRATION TEMPORARILY DISABLED”.
        Re-enabling never auto-resumes old queued or cancelled messages. Historical data, templates and logs are preserved.
      </p>
      <Link to="/admin/integrations/whatsapp" className="inline-flex items-center h-9 px-4 rounded-md bg-slate-900 text-white text-sm font-medium" data-testid="wa-open-integration">Open WhatsApp Integration (provider, templates, logs) →</Link>
    </div>
  );
}
